import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, interval, BehaviorSubject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { TokenService } from '@apps/auth/api/token.service';
import { AppConstants } from '@apps/app.constants';
import { Router } from '@angular/router';

export interface TokenStatus {
    isExpiringSoon: boolean;
    isExpired: boolean;
    remainingTime: number | null;
    expirationDate: Date | null;
}

@Injectable({
    providedIn: 'root'
})
export class TokenMonitorService implements OnDestroy {
    private expirationThreshold = 5; // Warn when less than 5 minutes remaining
    private expirationDate: Date | null;
    private timeouts: any[] = [];
    
    private tokenStatusSubject = new BehaviorSubject<TokenStatus>({
        isExpiringSoon: false,
        isExpired: false,
        remainingTime: null,
        expirationDate: null
    });

    public tokenStatus$ = this.tokenStatusSubject.asObservable();

    constructor(
        private tokenService: TokenService,
        private router: Router
    ) {
        this.startMonitoring();
    }

    ngOnDestroy(): void {
        this.tokenStatusSubject.complete();
    }

    private startMonitoring(): void {
        const checkToken = interval(500).subscribe(() => {
            const expiration = this.tokenService.getAccessTokenExpirationDate();
            if (expiration) {
                this.expirationDate = expiration;
                checkToken.unsubscribe();
                this.timeouts.forEach(timeout => clearTimeout(timeout));
                this.timeouts = [];
                this.scheduleChecks();
            }
        });
    }

    private scheduleChecks(): void {
        if (!this.expirationDate) {
            return;
        }

        const remainingTime = this.expirationDate.getTime() - new Date().getTime();
        if (remainingTime <= 0) {
            this.tokenStatusSubject.next({
                isExpiringSoon: false,
                isExpired: true,
                remainingTime: remainingTime,
                expirationDate: this.expirationDate
            });
        }

        // Trigger the display of the banner at the right time
        const timeout = setTimeout(() => {
            this.tokenStatusSubject.next({
                isExpiringSoon: false,
                isExpired: true,
                remainingTime: remainingTime,
                expirationDate: this.expirationDate  
            });
        }, remainingTime);
        this.timeouts.push(timeout);

        // If the token expires in less than 5 minutes, display the banner immediately, otherwise, prepare the display of the banner 5 minutes before the end of the session
        if (remainingTime <= this.expirationThreshold * 60 * 1000) {
            this.tokenStatusSubject.next({
                isExpiringSoon: true,
                isExpired: false,
                remainingTime: remainingTime,
                expirationDate: this.expirationDate
            });
        } else {
            this.tokenStatusSubject.next({
                isExpiringSoon: false,
                isExpired: false,
                remainingTime: remainingTime,
                expirationDate: this.expirationDate
            });
            const timeout = setTimeout(() => {
                this.tokenStatusSubject.next({
                    isExpiringSoon: true,
                    isExpired: false,
                    remainingTime: remainingTime,
                    expirationDate: this.expirationDate
                });
            }, remainingTime - this.expirationThreshold * 60 * 1000);
            this.timeouts.push(timeout);
        }

    }

    handleSessionAction(): void {
        const tokenStatus = this.tokenStatusSubject.value;
        this.timeouts.forEach(timeout => clearTimeout(timeout));
        this.timeouts = [];
        if (tokenStatus?.isExpired) {
            window.location.href = AppConstants.LoginUrl;
        } else {
            this.refreshToken().subscribe({
                next: (success) => {
                    if (!success) {
                        window.location.href = AppConstants.LoginUrl;
                    }
                },
                error: (err) => {
                    window.location.href = AppConstants.LoginUrl;
                }
            });
        }
    }

    public refreshToken(): Observable<boolean> {
        if (!this.tokenService.getRefreshToken()) {
            localStorage.removeItem('token');
            window.location.href = AppConstants.LoginUrl;
            return of(false);
        }

        return this.tokenService.getNewToken().pipe(
            map(newToken => {
                if (newToken) {
                    
                    this.startMonitoring();
                    return true;
                } else {
                    localStorage.removeItem('token');
                    window.location.href = AppConstants.LoginUrl;
                    return false;
                }
            })
        );
    }
}
